/* Mist House Fashion — Best Sellers V8
 * Scoped carousels, responsive variant previews and cart-aware add buttons.
 */
(function () {
  'use strict';

  const initialized = new WeakSet();
  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const imageCache = new Map();
  const allSections = new Set();
  let cartSnapshot = null;
  let cartUrl = null;
  let cartRequest = null;
  let cartRefreshQueued = false;
  let refreshTimer = null;
  let cartObserver = null;
  let watchedDrawer = null;
  let cartListenersReady = false;

  function loadImage(url) {
    if (!url) return null;
    if (imageCache.has(url)) return imageCache.get(url);
    if (imageCache.size >= 32) imageCache.delete(imageCache.keys().next().value);
    const entry = { ready: false, success: false, promise: null };
    entry.promise = new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = async function () {
        try { if (image.decode) await image.decode(); } catch (e) { /* Still usable. */ }
        entry.ready = true;
        entry.success = true;
        resolve(true);
      };
      image.onerror = function () {
        entry.ready = true;
        entry.success = false;
        imageCache.delete(url); // Retry if a temporary network failure occurred.
        resolve(false);
      };
      image.src = url;
    });
    imageCache.set(url, entry);
    return entry;
  }

  function showCartState(section, inCart) {
    section.querySelectorAll('[data-bs-card][data-product-id]').forEach(card => {
      const button = card.querySelector('[data-bs-cart]');
      if (!button) return;
      const hasProduct = inCart.has(Number(card.dataset.productId));
      button.classList.toggle('is-added', hasProduct);
      button.setAttribute('aria-pressed', hasProduct ? 'true' : 'false');
      button.setAttribute('aria-label', (hasProduct ? 'In bag. Add another ' : 'Add ') + card.dataset.productTitle + ' to bag');
    });
  }

  function applyCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    cartSnapshot = cart;
    const inCart = new Set(cart.items.map(item => Number(item.product_id)));
    allSections.forEach(section => {
      if (document.contains(section)) showCartState(section, inCart);
      else allSections.delete(section);
    });
    if (typeof cart.item_count === 'number') {
      document.querySelectorAll('.cart-link__bubble').forEach(bubble => {
        bubble.classList.toggle('cart-link__bubble--visible', cart.item_count > 0);
      });
    }
  }

  function markAdded(productId) {
    const ids = new Set(cartSnapshot && cartSnapshot.items ? cartSnapshot.items.map(item => Number(item.product_id)) : []);
    ids.add(Number(productId));
    allSections.forEach(section => showCartState(section, ids));
  }

  function fetchCart() {
    if (!cartUrl) return Promise.resolve(null);
    if (cartRequest) {
      cartRefreshQueued = true;
      return cartRequest;
    }
    cartRequest = fetch(cartUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(response => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      })
      .then(cart => {
        applyCart(cart);
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        cartRequest = null;
        if (cartRefreshQueued) {
          cartRefreshQueued = false;
          // A cart change may have completed while the earlier GET was pending.
          fetchCart();
        }
      });
    return cartRequest;
  }

  function queueCartRefresh(delay) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(fetchCart, delay == null ? 180 : delay);
  }

  function attachDrawerObserver() {
    const drawer = document.querySelector('#CartDrawer, cart-drawer, #cart-drawer, .drawer--cart, [data-cart-drawer]');
    if (drawer === watchedDrawer) return;
    if (cartObserver) cartObserver.disconnect();
    watchedDrawer = drawer;
    if (!drawer || typeof MutationObserver === 'undefined') return;
    cartObserver = new MutationObserver(() => queueCartRefresh(220));
    cartObserver.observe(drawer, { childList: true, subtree: true, characterData: true });
  }

  function connectCartListeners() {
    if (cartListenersReady) return;
    cartListenersReady = true;
    [
      'cart:updated', 'cart:change', 'cart:refresh',
      'cart:build', 'theme:cart:update', 'ajaxProduct:added'
    ].forEach(name => {
      document.addEventListener(name, event => {
        if (event.detail && event.detail.cart && Array.isArray(event.detail.cart.items)) {
          applyCart(event.detail.cart);
        } else {
          queueCartRefresh(160);
        }
        attachDrawerObserver();
      });
      window.addEventListener(name, event => {
        if (event.detail && event.detail.cart && Array.isArray(event.detail.cart.items)) {
          applyCart(event.detail.cart);
        } else {
          queueCartRefresh(160);
        }
      });
    });
    window.addEventListener('focus', () => queueCartRefresh(100));
    window.addEventListener('pageshow', () => queueCartRefresh(120));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) queueCartRefresh(100);
    });
    // Also support Impulse/cart apps that update the drawer without emitting events.
    document.addEventListener('click', event => {
      const target = event.target.closest('a,button,[role="button"]');
      if (!target) return;
      const inCartUI = target.closest('#CartDrawer, cart-drawer, #cart-drawer, .drawer--cart, [data-cart-drawer], form[action*="/cart"]');
      const cartAction = target.matches('[data-cart-remove], [data-cart-quantity], .cart__remove, [href*="/cart/change"], [data-qty-adjust]');
      if (inCartUI || cartAction) {
        attachDrawerObserver();
        setTimeout(fetchCart, 500);
        setTimeout(fetchCart, 1600);
      }
    });
    document.addEventListener('change', event => {
      const target = event.target;
      if (target && target.closest('#CartDrawer, cart-drawer, #cart-drawer, .drawer--cart, [data-cart-drawer], form[action*="/cart"]')) {
        setTimeout(fetchCart, 500);
        setTimeout(fetchCart, 1600);
      }
    });
    document.addEventListener('shopify:section:load', () => {
      attachDrawerObserver();
      queueCartRefresh(300);
    });
    attachDrawerObserver();
  }

  // Desktop pointer support. Each scroller is independent, including the colour
  // palette inside an individual product card. A drag never activates a product
  // link or colour button accidentally.
  function enableMouseScroll(scroller, section, isSwatchScroller) {
    if (!scroller) return;
    const isNestedSwatch = target => Boolean(target.closest('[data-bs-swatches]'));
    const canScroll = () => scroller.scrollWidth > scroller.clientWidth + 3;
    const mouseDragEnabled = () => section.dataset.bsMouseDrag === 'true' && !mobileQuery.matches;
    const mouseWheelEnabled = () => section.dataset.bsMouseWheel === 'true' && !mobileQuery.matches;
    let drag = null;
    let blockClickUntil = 0;

    function endDrag() {
      if (!drag) return;
      if (drag.moved) blockClickUntil = Date.now() + 450;
      drag = null;
      scroller.classList.remove('is-mouse-dragging');
    }

    scroller.addEventListener('mousedown', event => {
      if (!mouseDragEnabled() || event.button !== 0 || !canScroll()) return;
      if (!isSwatchScroller && isNestedSwatch(event.target)) return;
      if (!isSwatchScroller && event.target.closest('button, input, select, textarea, [data-bs-variant-popover]')) return;
      if (event.target.closest('[data-bs-cart]')) return;
      drag = { x: event.clientX, left: scroller.scrollLeft, moved: false };
    });

    // Listen on the document so dragging still works if the pointer leaves the
    // image/card. We only prevent text selection after a real drag starts.
    document.addEventListener('mousemove', event => {
      if (!drag) return;
      const distance = event.clientX - drag.x;
      if (!drag.moved && Math.abs(distance) < 6) return;
      drag.moved = true;
      if (event.cancelable) event.preventDefault();
      scroller.classList.add('is-mouse-dragging');
      scroller.scrollLeft = drag.left - distance;
    });
    document.addEventListener('mouseup', endDrag);
    window.addEventListener('blur', endDrag);
    scroller.addEventListener('dragstart', event => {
      if (mouseDragEnabled() && canScroll()) event.preventDefault();
    });

    // Capture the click before product links or swatch listeners receive it.
    // Clicks and keyboard activation without dragging work normally.
    scroller.addEventListener('click', event => {
      if (event.detail > 0 && Date.now() < blockClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
        blockClickUntil = 0;
      }
    }, true);

    // IMPORTANT: a plain vertical wheel must always scroll the PAGE, never
    // capture the wheel to move this horizontal carousel. Browsers/trackpads
    // already support natural horizontal deltas on overflow-x scrollers.
    // Only an explicit Shift + wheel gesture opts into horizontal movement.
    scroller.addEventListener('wheel', event => {
      if (!mouseWheelEnabled() || !canScroll() || event.ctrlKey || !event.shiftKey) return;
      // Shift + wheel over a nested colour palette controls only that palette.
      if (!isSwatchScroller && isNestedSwatch(event.target)) return;
      const raw = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const multiplier = event.deltaMode === 1 ? 18 : (event.deltaMode === 2 ? scroller.clientWidth : 1);
      const delta = raw * multiplier;
      if (Math.abs(delta) < 0.1) return;
      const maximum = scroller.scrollWidth - scroller.clientWidth;
      const next = Math.max(0, Math.min(maximum, scroller.scrollLeft + delta));
      if (Math.abs(next - scroller.scrollLeft) < 0.5) return;
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
      scroller.scrollLeft = next;
    }, { passive: false });
  }

  function setup(section) {
    if (initialized.has(section)) return;
    initialized.add(section);
    allSections.add(section);
    cartUrl = section.dataset.cartUrl || cartUrl;
    const needsCartSync = Boolean(section.querySelector('[data-bs-cart]'));
    if (needsCartSync) {
      connectCartListeners();
      if (cartSnapshot) applyCart(cartSnapshot);
      else fetchCart();
    }

    const viewport = section.querySelector('[data-bs-viewport]');
    const track = section.querySelector('[data-bs-track]');
    const prev = section.querySelector('[data-bs-prev]');
    const next = section.querySelector('[data-bs-next]');
    const pagination = section.querySelector('[data-bs-pagination]');
    if (!viewport || !track || !pagination || !prev || !next) return;
    const cards = Array.from(section.querySelectorAll('[data-bs-card]'));
    enableMouseScroll(viewport, section, false);
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const behavior = () => preference.matches ? 'auto' : 'smooth';
    const scrollMax = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const activePagination = () => mobileQuery.matches ? section.dataset.mobilePagination : section.dataset.desktopPagination;
    const showNav = () => mobileQuery.matches ? section.dataset.mobileNav === 'true' : section.dataset.desktopNav === 'true';

    function itemStep() {
      const first = cards[0];
      if (!first) return viewport.clientWidth;
      const cs = window.getComputedStyle(track);
      return first.getBoundingClientRect().width + (parseFloat(cs.gap) || 0);
    }
    function countPages() {
      if (!scrollMax()) return 1;
      return Math.max(1, Math.ceil(scrollMax() / itemStep()) + 1);
    }
    function currentPage() {
      if (!scrollMax()) return 0;
      return Math.min(countPages() - 1, Math.round(viewport.scrollLeft / itemStep()));
    }
    function updateArrows() {
      const canScroll = scrollMax() > 3;
      prev.hidden = !canScroll || !showNav();
      next.hidden = !canScroll || !showNav();
      prev.disabled = viewport.scrollLeft <= 2;
      next.disabled = viewport.scrollLeft >= scrollMax() - 2;
    }
    function updatePagination() {
      const type = activePagination();
      const max = scrollMax();
      if (type === 'none' || max < 3) {
        pagination.hidden = true;
        return;
      }
      pagination.hidden = false;
      if (type === 'bar') {
        if (!pagination.classList.contains('mh-bs__pagination--bar')) {
          pagination.innerHTML = '<span class="mh-bs__progress" data-bs-progress></span>';
          pagination.classList.add('mh-bs__pagination--bar');
        }
        const fill = pagination.querySelector('[data-bs-progress]');
        const ratio = viewport.clientWidth / viewport.scrollWidth;
        const width = Math.max(ratio * 100, 12);
        const left = max ? (viewport.scrollLeft / max) * (100 - width) : 0;
        fill.style.width = width + '%';
        fill.style.transform = 'translateX(' + (left / Math.max(width, 1) * 100) + '%)';
      } else {
        pagination.classList.remove('mh-bs__pagination--bar');
        const pages = countPages();
        if (pagination.children.length !== pages || pagination.querySelector('[data-bs-progress]')) {
          pagination.replaceChildren();
          for (let i = 0; i < pages; i++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'mh-bs__page-dot';
            dot.setAttribute('aria-label', 'Show products page ' + (i + 1));
            dot.addEventListener('click', () => viewport.scrollTo({left:Math.min(i * itemStep(), scrollMax()), behavior:behavior()}));
            pagination.appendChild(dot);
          }
        }
        Array.from(pagination.children).forEach((dot, i) => {
          const active = i === currentPage();
          dot.classList.toggle('is-current', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }
    }
    function redraw() {
      updateArrows(); updatePagination();
      const picture = cards[0] && cards[0].querySelector('.mh-bs__picture');
      const navigation = section.querySelector('.mh-bs__carousel-navigation');
      if (picture && navigation) navigation.style.height = Math.round(picture.getBoundingClientRect().height) + 'px';
    }
    prev.addEventListener('click', () => viewport.scrollBy({left:-itemStep(), behavior:behavior()}));
    next.addEventListener('click', () => viewport.scrollBy({left:itemStep(), behavior:behavior()}));
    viewport.addEventListener('scroll', redraw, {passive:true});
    window.addEventListener('resize', redraw, {passive:true});
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(redraw);
      observer.observe(viewport);
      observer.observe(track);
    }
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', redraw);
    requestAnimationFrame(redraw);

    const cartAddUrl = section.dataset.cartAddUrl;

    async function addToCart(variantId, card, button) {
      if (!variantId || !cartAddUrl || !cartUrl) return;
      if (button) { button.classList.add('is-loading'); button.disabled = true; }
      const status = card.querySelector('[data-bs-status]');
      if (status) { status.textContent = ''; status.classList.remove('is-error'); }
      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        const added = await response.json();
        if (!response.ok) throw new Error(added.description || added.message || 'This item could not be added.');
        markAdded(card.dataset.productId);
        if (status) status.textContent = 'Added to bag';
        const updatedCart = await fetchCart();
        if (updatedCart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: added }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('mh:best-sellers:added', { detail: { product: added }, bubbles: true }));
      } catch (error) {
        if (status) { status.textContent = error.message || 'Unable to add. Please try again.'; status.classList.add('is-error'); }
      } finally {
        if (button) { button.classList.remove('is-loading'); button.disabled = false; }
      }
    }

    cards.forEach(card => {
      const cartButton = card.querySelector('[data-bs-cart]');
      const panel = card.querySelector('[data-bs-variant-popover]');
      const selector = card.querySelector('[data-bs-variant-select]');
      const submit = card.querySelector('[data-bs-submit]');
      const cancel = card.querySelector('[data-bs-close]');
      const status = card.querySelector('[data-bs-status]');
      const swatches = card.querySelector('[data-bs-swatches]');
      const price = card.querySelector('[data-bs-price]');
      const compare = card.querySelector('[data-bs-compare-price]');
      const mainImage = card.querySelector('.mh-bs__image:not(.mh-bs__image--hover)');
      const originalSrc = mainImage ? mainImage.getAttribute('src') : null;
      const originalSrcset = mainImage ? mainImage.getAttribute('srcset') : null;
      const originalSizes = mainImage ? mainImage.getAttribute('sizes') : null;
      let selectedVariant = card.dataset.selectedVariant;
      let imageRequestId = 0;

      function setVariantImage(url) {
        if (!mainImage) return;
        const requestId = ++imageRequestId;
        if (!url || url === originalSrc) {
          card.classList.remove('has-variant-image');
          mainImage.src = originalSrc || '';
          if (originalSrcset) mainImage.setAttribute('srcset', originalSrcset);
          if (originalSizes) mainImage.setAttribute('sizes', originalSizes);
          return;
        }
        const preview = loadImage(url);
        const commit = () => {
          if (requestId !== imageRequestId || !preview.success) return;
          // Cached or preloaded image can be displayed without waiting for a new download.
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
          mainImage.src = url;
          card.classList.add('has-variant-image');
        };
        if (preview.ready) commit();
        else preview.promise.then(commit);
      }

      if (cartButton) {
        cartButton.addEventListener('click', () => {
          if (card.dataset.requiresChoice === 'true') {
            if (!panel) return;
            panel.hidden = !panel.hidden;
            cartButton.setAttribute('aria-expanded', String(!panel.hidden));
            if (!panel.hidden && selector) selector.focus();
          } else {
            addToCart(selectedVariant, card, cartButton);
          }
        });
      }
      if (submit) submit.addEventListener('click', () => {
        if (!selector || !selector.value) return;
        addToCart(selector.value, card, submit);
        if (panel) panel.hidden = true;
        if (cartButton) cartButton.setAttribute('aria-expanded', 'false');
      });
      if (cancel) cancel.addEventListener('click', () => {
        if (panel) panel.hidden = true;
        if (cartButton) cartButton.setAttribute('aria-expanded', 'false');
      });
      if (selector) selector.addEventListener('change', () => {
        selectedVariant = selector.value;
        if (price) price.textContent = selector.selectedOptions[0].dataset.price || price.textContent;
        if (compare) {
          compare.textContent = selector.selectedOptions[0].dataset.compare || '';
          compare.hidden = !selector.selectedOptions[0].dataset.compare;
        }
      });

      const swatchButtons = Array.from(card.querySelectorAll('[data-bs-swatch]'));
      swatchButtons.forEach(swatch => {
        // Ensure the only selection indicator is on the clicked colour in THIS card.
        // The browser focus ring is separately styled for keyboard access.
        // A press begins preloading even before the click event fires.
        swatch.addEventListener('pointerdown', () => loadImage(swatch.dataset.bsVariantImage));
        swatch.addEventListener('mouseenter', () => loadImage(swatch.dataset.bsVariantImage));
        swatch.addEventListener('focus', () => loadImage(swatch.dataset.bsVariantImage));
        swatch.addEventListener('click', event => {
          // No other swatch on the same product can remain visually selected.
          // Never modify the state of a neighbouring product card.
          if (swatch.disabled) return;
          event.stopPropagation();
          swatchButtons.forEach(button => {
            const selected = button === swatch;
            button.classList.toggle('is-selected', selected);
            button.setAttribute('aria-pressed', String(selected));
          });
          // Pointer clicks shouldn't leave a second, stale keyboard-focus ring.
          // Keep focus for keyboard activation (event.detail === 0).
          if (event.detail > 0) swatch.blur();
          if (swatch.dataset.bsVariantId) {
            selectedVariant = swatch.dataset.bsVariantId;
            if (selector) selector.value = selectedVariant;
          }
          if (price && swatch.dataset.bsVariantPrice) price.textContent = swatch.dataset.bsVariantPrice;
          if (compare) {
            compare.textContent = swatch.dataset.bsVariantCompare || '';
            compare.hidden = !swatch.dataset.bsVariantCompare;
          }
          setVariantImage(swatch.dataset.bsVariantImage || null);
          if (status) { status.textContent = ''; status.classList.remove('is-error'); }
          // Don't clear is-added: it reflects actual Shopify cart membership, not the swatch clicked.
        });
      });

      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const slowConnection = connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''));
      if (section.dataset.bsPrefetch === 'true' && !slowConnection && swatchButtons.length > 0) {
        const limit = Math.max(1, Number(section.dataset.bsPrefetchLimit) || 2);
        const toPreload = swatchButtons.map(btn => btn.dataset.bsVariantImage)
          .filter(Boolean).filter((url, i, all) => all.indexOf(url) === i).slice(0, limit);
        const warmUp = () => {
          const run = () => toPreload.forEach(loadImage);
          if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 });
          else setTimeout(run, 500);
        };
        if (typeof IntersectionObserver !== 'undefined') {
          const observer = new IntersectionObserver((entries) => {
            if (entries.some(entry => entry.isIntersecting)) {
              observer.disconnect();
              warmUp();
            }
          }, { root: viewport, threshold: 0.25 });
          observer.observe(card);
        } else warmUp();
      }

      if (swatches) {
        enableMouseScroll(swatches, section, true);
        const left = card.querySelector('[data-bs-swatch-prev]');
        const right = card.querySelector('[data-bs-swatch-next]');
        const updateSwatches = () => {
          if (!left || !right) return;
          const overflow = swatches.scrollWidth > swatches.clientWidth + 2;
          left.hidden = right.hidden = !overflow;
          if (overflow) {
            left.disabled = swatches.scrollLeft < 2;
            right.disabled = swatches.scrollLeft >= swatches.scrollWidth - swatches.clientWidth - 2;
          }
        };
        if (left && right) {
          left.addEventListener('click', () => swatches.scrollBy({left:-100, behavior:behavior()}));
          right.addEventListener('click', () => swatches.scrollBy({left:100, behavior:behavior()}));
          swatches.addEventListener('scroll', updateSwatches, {passive:true});
          window.addEventListener('resize', updateSwatches, {passive:true});
          requestAnimationFrame(updateSwatches);
        }
      }
    });
    document.addEventListener('click', event => {
      cards.forEach(card => {
        const panel = card.querySelector('[data-bs-variant-popover]');
        if (panel && !panel.hidden && !card.contains(event.target)) {
          panel.hidden = true;
          const button = card.querySelector('[data-bs-cart]');
          if (button) button.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-mh-best-sellers]').forEach(setup);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
